import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const FAQSection = ({ faq }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!faq || faq.length === 0) return null;

  return (
    <section className="py-6 lg:py-8 ">
      <div className="max-w-[1700px] mx-auto px-6 sm:px-8 lg:px-10 xl:px-12">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <div className="inline-flex items-center rounded-full bg-gradient-to-r from-orange-100 via-pink-100 to-purple-100 px-3 py-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-orange-600">
            FAQ
          </div>

          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-gray-900">
            Frequently Asked Questions
          </h2>

          <div className="mt-3 h-[3px] w-16 rounded-full bg-gradient-to-r from-[#FF7A18] via-[#FF5B8A] to-[#9C4DFF]" />

          <p className="mt-5 max-w-5xl text-[15px] sm:text-base leading-7 text-gray-600">
            Find answers to common questions about our AI services,
            implementation process, enterprise solutions, and how we help
            businesses accelerate digital transformation.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faq.map((item, index) => {
            const isOpen = activeIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.35,
                  delay: index * 0.05,
                }}
                viewport={{ once: true }}
                className={`overflow-hidden rounded-xl border transition-all duration-300 ${
                  isOpen
                    ? "border-[#EC4899]/40 bg-gradient-to-r from-orange-50/70 via-pink-50/70 to-purple-50/70 shadow-sm"
                    : "border-gray-200 bg-white hover:border-[#EC4899]/30 hover:shadow-sm"
                }`}
              >
                <button
                  onClick={() => setActiveIndex(isOpen ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                >
                  <h3 className="flex-1 text-[15px] sm:text-base font-semibold leading-6 text-gray-900">
                    {item.q}
                  </h3>

                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-all ${
                      isOpen
                        ? "bg-gradient-to-r from-[#FF7A18] via-[#FF5B8A] to-[#9C4DFF] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isOpen ? (
                      <Minus size={16} />
                    ) : (
                      <Plus size={16} />
                    )}
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-pink-100 px-5 sm:px-6 py-4">
                        <p className="text-[14px] sm:text-[15px] leading-7 text-gray-600">
                          {item.a}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;